import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, Prohibit } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CIERRE_META = {
  realizada: {
    label: 'Realizada',
    verb: 'Marcar como Realizada',
    color: '#065F46',
    hover: '#064e3b',
    description: 'Confirma que la movilización se ejecutó con normalidad.',
    icon: CheckCircle,
  },
  suspendida: {
    label: 'Suspendida',
    verb: 'Suspender Movilización',
    color: '#78716C',
    hover: '#57534E',
    description: 'Indica que la movilización fue cancelada o no pudo ejecutarse.',
    icon: Prohibit,
  },
};

export const CierreButtons = ({ movilizacion, onOpen, testIdPrefix = '' }) => {
  if (movilizacion.estado !== 'asignado') return null;
  const idPrefix = testIdPrefix ? `${testIdPrefix}-` : '';
  return (
    <>
      <Button
        data-testid={`${idPrefix}realizada-button`}
        onClick={() => onOpen('realizada')}
        size="sm"
        className="gap-2 bg-[#065F46] hover:bg-[#064e3b] text-white font-['IBM_Plex_Sans'] transition-colors"
      >
        <CheckCircle size={16} weight="bold" /> Realizado
      </Button>
      <Button
        data-testid={`${idPrefix}suspendida-button`}
        onClick={() => onOpen('suspendida')}
        size="sm"
        variant="outline"
        className="gap-2 border-[#78716C] text-[#78716C] hover:bg-[#78716C] hover:text-white font-['IBM_Plex_Sans'] transition-colors"
      >
        <Prohibit size={16} weight="bold" /> Suspendido
      </Button>
    </>
  );
};

export const CierreDialog = ({ movilizacion, cierreAction, open, onOpenChange, onSuccess }) => {
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setComentario('');
  }, [open]);

  if (!movilizacion || !cierreAction) return null;
  const meta = CIERRE_META[cierreAction];
  if (!meta) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (cierreAction === 'suspendida' && !comentario.trim()) {
      toast.error('Indica el motivo de la suspensión');
      return;
    }
    setLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/movilizaciones/${movilizacion.id}/cierre`,
        { estado: cierreAction, comentario_cierre: comentario.trim() || null },
        { withCredentials: true }
      );
      toast.success(`Movilización marcada como ${meta.label}`);
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
            {meta.verb}
          </DialogTitle>
          <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
            {meta.description}
          </DialogDescription>
        </DialogHeader>

        <form data-testid={`cierre-form-${cierreAction}`} onSubmit={submit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="comentario-cierre" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
              Comentario {cierreAction === 'suspendida' ? '*' : '(opcional)'}
            </Label>
            <Textarea
              id="comentario-cierre"
              data-testid="comentario-cierre-input"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              placeholder={
                cierreAction === 'realizada'
                  ? 'Ej: Se cumplió la ruta prevista sin novedades'
                  : 'Motivo de la suspensión (obligatorio)'
              }
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans'] min-h-[100px]"
              required={cierreAction === 'suspendida'}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="confirm-cierre-button"
              disabled={loading}
              className="flex-1 text-white font-['IBM_Plex_Sans'] transition-colors"
              style={{ backgroundColor: meta.color }}
            >
              {loading ? 'Guardando...' : `Confirmar ${meta.label}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
